/**
 * Assainissement du HTML des blocs de texte enrichi (TipTap).
 *
 * Le contenu d'un bloc `rich_text` est persisté en HTML puis rendu via
 * `dangerouslySetInnerHTML`. Même si l'auteur du rapport est un utilisateur
 * authentifié, le rapport est consulté par les autres membres du projet : un
 * contenu piégé exécuterait du script dans la session du lecteur (XSS stocké).
 *
 * L'assainissement est délégué à **DOMPurify**, audité en continu et durci
 * contre les mXSS découvertes après coup. La liste blanche maison précédente
 * n'avait pas de bypass connu, mais elle ne suivait pas les nouvelles mutations.
 *
 * DOMPurify est ici **restreint** par rapport à sa configuration par défaut :
 *  - liste blanche explicite de balises (celles que produit notre TipTap) :
 *    ni SVG, ni MathML, ni `<script>`, ni `<iframe>`, ni `<img>` ;
 *  - liste blanche d'attributs **par balise** (hook `uponSanitizeAttribute`) :
 *    DOMPurify n'autorise qu'une liste globale, on la resserre ;
 *  - liste blanche de propriétés CSS dans `style` : DOMPurify ne filtre pas le
 *    CSS déclaration par déclaration (il s'appuie sur le fait que le CSS moderne
 *    n'exécute plus de JS), on conserve donc notre filtre, plus strict ;
 *  - `href` restreint à http/https/mailto/tel, aux ancres et aux chemins
 *    relatifs **non protocol-relative** (`//evil.com` et `/\evil.com` sont
 *    traités comme des URLs absolues par les navigateurs : redirection externe
 *    déguisée).
 */
import DOMPurify from 'dompurify';

/** Balises conservées (celles que produit notre configuration TipTap). */
const ALLOWED_TAGS = [
  'p', 'br', 'span', 'div',
  'strong', 'b', 'em', 'i', 'u', 's', 'strike', 'del', 'mark', 'sub', 'sup',
  'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
  'ul', 'ol', 'li',
  'blockquote', 'code', 'pre', 'hr',
  'a',
];

/** Attributs autorisés, par balise. `style` est traité à part (filtrage par propriété). */
const ALLOWED_ATTRS_BY_TAG: Record<string, Set<string>> = {
  A: new Set(['href', 'target', 'rel', 'title']),
  MARK: new Set(['data-color']),
};

/** Union des attributs ci-dessus + `style` : la liste globale que voit DOMPurify. */
const ALLOWED_ATTR = ['href', 'target', 'rel', 'title', 'data-color', 'style'];

/** Propriétés CSS autorisées dans un attribut `style`. */
const ALLOWED_STYLE_PROPS = new Set(['color', 'background-color', 'text-align']);

/**
 * Protocoles et formes d'URL acceptés pour `href`.
 *
 * Autorise : `https:`/`http:`/`mailto:`/`tel:`, les ancres (`#x`), les requêtes
 * (`?x`), les chemins absolus simples (`/rapport/12`) et les chemins relatifs
 * (`page.html`).
 *
 * Rejette : tout autre schéma (`javascript:`, `data:`, `vbscript:`…) et les URLs
 * *protocol-relative* — `/` suivi de `/` ou de `\`, que les navigateurs
 * résolvent en URL absolue vers un hôte tiers.
 *
 * (DOMPurify normalise la valeur — espaces et caractères de contrôle retirés —
 * avant de la confronter à cette expression, ce qui neutralise `java\tscript:`.)
 */
const ALLOWED_URI_REGEXP = /^(?:(?:https?|mailto|tel):|[#?]|\/(?![/\\])|[\w.~%+-]+(?:[/?#]|$))/i;

/** Ne garde que les déclarations CSS de la liste blanche, et jamais de `url(...)` / `expression(...)`. */
function sanitizeStyle(style: string): string {
  return style
    .split(';')
    .map((declaration) => declaration.trim())
    .filter(Boolean)
    .map((declaration) => {
      const idx = declaration.indexOf(':');
      if (idx === -1) return null;
      const prop = declaration.slice(0, idx).trim().toLowerCase();
      const value = declaration.slice(idx + 1).trim();
      if (!ALLOWED_STYLE_PROPS.has(prop)) return null;
      if (/url\s*\(|expression\s*\(|javascript:/i.test(value)) return null;
      return `${prop}: ${value}`;
    })
    .filter((d): d is string => d !== null)
    .join('; ');
}

/**
 * Resserre la liste blanche d'attributs de DOMPurify (globale) en une liste
 * blanche **par balise**, et filtre le CSS déclaration par déclaration.
 */
DOMPurify.addHook('uponSanitizeAttribute', (node, data) => {
  const name = data.attrName;

  if (name === 'style') {
    const style = sanitizeStyle(data.attrValue);
    if (!style) {
      data.keepAttr = false;
      return;
    }
    data.attrValue = style;
    return;
  }

  const allowed = ALLOWED_ATTRS_BY_TAG[node.nodeName?.toUpperCase() ?? ''];
  if (!allowed || !allowed.has(name)) data.keepAttr = false;
});

/** Un lien qui s'ouvre dans un nouvel onglet sans `rel` expose `window.opener`. */
DOMPurify.addHook('afterSanitizeAttributes', (node) => {
  if (node.nodeName === 'A' && node.hasAttribute('href')) {
    node.setAttribute('rel', 'noopener noreferrer');
  }
});

/** Assainit le HTML d'un bloc de texte enrichi avant insertion en `dangerouslySetInnerHTML`. */
export function sanitizeRichText(html: string): string {
  if (!html || typeof html !== 'string') return '';

  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS,
    ALLOWED_ATTR,
    ALLOWED_URI_REGEXP,
    // Aucun `data-*` / `aria-*` implicite : seul `data-color` (sur `<mark>`) passe.
    ALLOW_DATA_ATTR: false,
    ALLOW_ARIA_ATTR: false,
    // Pas de `<svg>` / `<math>` : hors de `ALLOWED_TAGS`, et leurs sous-arbres
    // (comme `<script>`, `<style>`, `<noscript>`) sont retirés avec leur contenu.
    FORBID_TAGS: ['svg', 'math'],
    RETURN_DOM: false,
    RETURN_DOM_FRAGMENT: false,
  });
}
